import React, { useEffect, useMemo, useState } from "react";
import { api, UNIT_META } from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/ui-parts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DialogShell, Field, Input, Textarea, UnitSelect } from "@/components/form-helpers";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const todayISO = () => new Date().toISOString().slice(0, 10);
const STATUS_TONE = {
  open: "bg-amber-400/10 text-amber-400 border-amber-400/30",
  partial: "bg-blue-400/10 text-blue-400 border-blue-400/30",
  completed: "bg-emerald-400/10 text-emerald-400 border-emerald-400/30",
  cancelled: "bg-red-400/10 text-red-400 border-red-400/30",
};

export default function Sauda() {
  const [rows, setRows] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm());

  const load = async () => {
    const params = {};
    if (filterType !== "all") params.type = filterType;
    const r = await api.get("/sauda", { params });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [filterType]);

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { _key: `it_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, product: "", quantity: "", unit_of_measure: unitOfMeasure(f.sauda_type), rate: "" }] }));
  const removeItem = (key) => setForm((f) => ({ ...f, items: f.items.filter((it) => it._key !== key) }));
  const patchItem = (key, patch) => setForm((f) => ({ ...f, items: f.items.map((it) => it._key === key ? { ...it, ...patch } : it) }));

  const submit = async () => {
    try {
      const payload = {
        ...form,
        rate: Number(form.rate) || 0,
        items: form.items.map((i) => ({
          product: i.product,
          quantity: Number(i.quantity) || 0,
          unit_of_measure: i.unit_of_measure || unitOfMeasure(form.sauda_type),
          rate: Number(i.rate) || 0,
        })).filter((i) => i.product && i.quantity > 0),
      };
      if (!payload.unit || !payload.party_name || !payload.date) {
        toast.error("Fill unit, party name and date");
        return;
      }
      await api.post("/sauda", payload);
      toast.success("Sauda created");
      setOpen(false);
      setForm(initialForm());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/sauda/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const productOptions = useMemo(() => UNIT_META[form.unit]?.products || [], [form.unit]);

  return (
    <div>
      <PageHeader
        eyebrow="Trading"
        title="Sauda Register"
        description="Purchase & sale contracts across units — by weight, by vehicle or by bags. Track rate, party, broker and status."
        testid="sauda-header"
        actions={
          <Button data-testid="add-sauda-btn" onClick={() => setOpen(true)} className="bg-amber-400 hover:bg-amber-300 text-black rounded-none">
            <Plus className="mr-2" size={14} weight="bold" /> New Sauda
          </Button>
        }
      />

      <div className="flex gap-2 mb-4" data-testid="sauda-filter">
        {["all", "purchase", "sale"].map((k) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilterType(k)}
            className={`px-4 py-2 label-caps text-[10px] border transition-colors ${filterType === k ? "bg-amber-400 text-black border-amber-400" : "border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}
          >
            {k}
          </button>
        ))}
      </div>

      <Panel title={`Sauda Entries (${rows.length})`}>
        {rows.length === 0 ? (
          <EmptyState message="No sauda yet" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="label-caps">Sauda No</TableHead>
                <TableHead className="label-caps">Date</TableHead>
                <TableHead className="label-caps">Type</TableHead>
                <TableHead className="label-caps">Unit</TableHead>
                <TableHead className="label-caps">Sauda Type</TableHead>
                <TableHead className="label-caps">Party</TableHead>
                <TableHead className="label-caps">Broker</TableHead>
                <TableHead className="label-caps text-right">Rate</TableHead>
                <TableHead className="label-caps">For/Ex</TableHead>
                <TableHead className="label-caps">Items</TableHead>
                <TableHead className="label-caps">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="border-zinc-800" data-testid={`sauda-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-amber-400">{r.sauda_no}</TableCell>
                  <TableCell className="font-mono text-xs">{r.date}</TableCell>
                  <TableCell className="uppercase text-xs">{r.type}</TableCell>
                  <TableCell><span className="label-caps" style={{ color: UNIT_META[r.unit]?.accent }}>{UNIT_META[r.unit]?.tag}</span></TableCell>
                  <TableCell className="text-xs uppercase">{r.sauda_type?.replace("_", " ")}</TableCell>
                  <TableCell>{r.party_name}</TableCell>
                  <TableCell className="text-zinc-400">{r.broker_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{r.rate}</TableCell>
                  <TableCell className="text-zinc-400 text-xs">{r.for_ex || "—"}</TableCell>
                  <TableCell className="text-xs">
                    {(r.items || []).map((it, i) => (
                      <div key={`${r.id}-${it.product}-${i}`}>{it.product} · {it.quantity}{it.unit_of_measure}</div>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                      <SelectTrigger className={`rounded-none h-7 text-[10px] uppercase border px-2 ${STATUS_TONE[r.status]}`} data-testid={`status-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <DialogShell open={open} onOpenChange={setOpen} title="Create Sauda" onSubmit={submit} submitLabel="Book Sauda" testidSubmit="save-sauda-btn">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Unit"><UnitSelect testid="sauda-unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v, items: form.items.map((i) => ({ ...i, product: "" })) })} /></Field>
          <Field label="Type">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="rounded-none bg-zinc-950 border-zinc-800" data-testid="sauda-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="sale">Sale</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sauda Type">
            <Select value={form.sauda_type} onValueChange={(v) => setForm({ ...form, sauda_type: v, items: form.items.map((it) => ({ ...it, unit_of_measure: unitOfMeasure(v) })) })}>
              <SelectTrigger className="rounded-none bg-zinc-950 border-zinc-800" data-testid="sauda-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                <SelectItem value="by_weight">By Weight (kg)</SelectItem>
                <SelectItem value="by_bags">By Bags</SelectItem>
                <SelectItem value="by_vehicle">By Vehicle</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date"><Input data-testid="sauda-date" type="date" className="rounded-none bg-zinc-950 border-zinc-800" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Party Name"><Input data-testid="sauda-party" className="rounded-none bg-zinc-950 border-zinc-800" value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} placeholder="Buyer or Supplier" /></Field>
          <Field label="Broker Name"><Input data-testid="sauda-broker" className="rounded-none bg-zinc-950 border-zinc-800" value={form.broker_name} onChange={(e) => setForm({ ...form, broker_name: e.target.value })} /></Field>
          <Field label="Rate ₹"><Input data-testid="sauda-rate" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></Field>
          <Field label="For / Ex (delivery terms)"><Input data-testid="sauda-forex" className="rounded-none bg-zinc-950 border-zinc-800" value={form.for_ex} onChange={(e) => setForm({ ...form, for_ex: e.target.value })} placeholder="e.g. Ex-factory, For-Delhi" /></Field>
        </div>

        <div className="border border-zinc-800 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="label-caps">Items</div>
            <button onClick={addItem} data-testid="sauda-additem-btn" className="text-amber-400 text-xs uppercase tracking-[0.2em] hover:text-amber-300">+ Add item</button>
          </div>
          {form.items.length === 0 && <div className="text-xs text-zinc-500">No items — click Add item.</div>}
          {form.items.map((it, i) => (
            <div key={it._key} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                <Select value={it.product} onValueChange={(v) => patchItem(it._key, { product: v })} disabled={!form.unit}>
                  <SelectTrigger className="rounded-none bg-zinc-950 border-zinc-800" data-testid={`item-product-${i}`}><SelectValue placeholder={form.unit ? "Product" : "Pick unit first"} /></SelectTrigger>
                  <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                    {productOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-3">
                <Input type="number" step="0.01" placeholder={`Qty (${unitOfMeasure(form.sauda_type)})`} className="rounded-none bg-zinc-950 border-zinc-800" value={it.quantity} onChange={(e) => patchItem(it._key, { quantity: e.target.value })} data-testid={`item-qty-${i}`} />
              </div>
              <div className="col-span-3">
                <Input type="number" step="0.01" placeholder="Rate" className="rounded-none bg-zinc-950 border-zinc-800" value={it.rate} onChange={(e) => patchItem(it._key, { rate: e.target.value })} data-testid={`item-rate-${i}`} />
              </div>
              <button onClick={() => removeItem(it._key)} className="col-span-1 text-red-400 hover:text-red-300 h-10 grid place-items-center" aria-label="Remove">
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>

        <Field label="Remarks"><Textarea className="rounded-none bg-zinc-950 border-zinc-800" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
      </DialogShell>
    </div>
  );
}

function initialForm() {
  return {
    unit: "",
    type: "purchase",
    sauda_type: "by_weight",
    date: todayISO(),
    party_name: "",
    broker_name: "",
    rate: "",
    for_ex: "",
    status: "open",
    items: [],
    remarks: "",
  };
}

function unitOfMeasure(t) {
  if (t === "by_bags") return "bags";
  if (t === "by_vehicle") return "vehicles";
  return "kg";
}
